import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import {
    getUnreadCountApi,
    listNotificationsApi,
    markAllNotificationsReadApi,
    markNotificationReadApi,
} from './query';
import { resolveNotificationDestination } from './helpers';
import NotificationItem from './NotificationItem';

const FILTER_UNREAD = 'unread';
const FILTER_ALL = 'all';

/**
 * Full, paginated list of the current user's in-app notifications, with an Unread/All filter.
 *
 * Filtering happens server-side (`?unread=true`) since the list is cursor-paginated: a client-side
 * filter could show an empty first page while unread items sit further down. For the same reason
 * the "Mark all as read" enabled state comes from the unread-count endpoint, not from the loaded
 * page. Rows marked read while on the Unread tab stay in place (rendered as read) until the tab is
 * re-selected, so the list never shifts under the user's cursor.
 */
class NotificationsList extends Component {
    constructor(props) {
        super(props);

        this.onShowMore = this.onShowMore.bind(this);
        this.onMarkOneRead = this.onMarkOneRead.bind(this);
        this.onMarkAllRead = this.onMarkAllRead.bind(this);
        this.onItemClick = this.onItemClick.bind(this);

        // Guards against out-of-order responses when the user switches tabs quickly.
        this.listRequestId = 0;

        this.state = {
            filter: FILTER_UNREAD,
            notifications: [],
            nextPageUrl: null,
            unreadCount: 0,
            isLoading: true,
            isLoadingMore: false,
        };
    }

    componentDidMount() {
        this.fetchFirstPage();
        this.fetchUnreadCount();
    }

    async fetchUnreadCount() {
        try {
            const res = await getUnreadCountApi();
            if (!res.ok) return;
            const data = await res.json();
            this.setState({ unreadCount: data.unread });
        } catch (err) {
            console.log(err);
        }
    }

    async fetchFirstPage() {
        const requestId = ++this.listRequestId;
        this.setState({ isLoading: true, notifications: [], nextPageUrl: null });
        try {
            const res = await listNotificationsApi({ unread: this.state.filter === FILTER_UNREAD });
            if (!res.ok || requestId !== this.listRequestId) return;
            const data = await res.json();
            if (requestId !== this.listRequestId) return;
            this.setState({ notifications: data.results, nextPageUrl: data.next });
        } catch (err) {
            console.log(err);
        } finally {
            if (requestId === this.listRequestId) this.setState({ isLoading: false });
        }
    }

    onFilterChange(filter) {
        if (filter === this.state.filter) return;
        this.setState({ filter }, () => this.fetchFirstPage());
    }

    async onShowMore() {
        if (!this.state.nextPageUrl || this.state.isLoadingMore) return;
        const requestId = this.listRequestId;
        this.setState({ isLoadingMore: true });
        try {
            const res = await listNotificationsApi({ cursor: this.state.nextPageUrl });
            if (!res.ok || requestId !== this.listRequestId) return;
            const data = await res.json();
            if (requestId !== this.listRequestId) return;
            this.setState((prevState) => ({
                notifications: [...prevState.notifications, ...data.results],
                nextPageUrl: data.next,
            }));
        } catch (err) {
            console.log(err);
        } finally {
            this.setState({ isLoadingMore: false });
        }
    }

    markLocalNotificationRead(notificationId) {
        this.setState((prevState) => ({
            notifications: prevState.notifications.map((n) =>
                n.id === notificationId && !n.read_at
                    ? { ...n, read_at: new Date().toISOString() }
                    : n,
            ),
            unreadCount: Math.max(0, prevState.unreadCount - 1),
        }));
    }

    async onMarkOneRead(notification) {
        if (notification.read_at) return;
        this.markLocalNotificationRead(notification.id);
        try {
            await markNotificationReadApi(notification.id);
        } catch (err) {
            console.log(err);
        }
    }

    async onMarkAllRead() {
        this.setState((prevState) => ({
            notifications: prevState.notifications.map((n) =>
                n.read_at ? n : { ...n, read_at: new Date().toISOString() },
            ),
            unreadCount: 0,
        }));
        try {
            await markAllNotificationsReadApi();
        } catch (err) {
            console.log(err);
        }
    }

    onItemClick(notification) {
        if (!notification.read_at) this.markLocalNotificationRead(notification.id);

        const destination = resolveNotificationDestination(notification);
        if (destination) window.location.href = destination;
    }

    render() {
        const { t } = this.props;
        const { filter, notifications, nextPageUrl, unreadCount, isLoading, isLoadingMore } =
            this.state;
        const tabs = [
            { key: FILTER_UNREAD, label: t('layout.notifications.tab_unread') },
            { key: FILTER_ALL, label: t('layout.notifications.tab_all') },
        ];

        return (
            <div className="notifications-list">
                <div className="notifications-list-header">
                    <div className="notifications-list-tabs" role="tablist">
                        {tabs.map((tab) => (
                            <button
                                key={tab.key}
                                type="button"
                                role="tab"
                                aria-selected={filter === tab.key}
                                className={`notifications-list-tab ${
                                    filter === tab.key ? 'is-active' : ''
                                }`}
                                onClick={() => this.onFilterChange(tab.key)}
                            >
                                {tab.label}
                                {tab.key === FILTER_UNREAD && unreadCount > 0 && (
                                    <span className="notifications-list-tab-count">
                                        {unreadCount > 99 ? '99+' : unreadCount}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        className="notifications-list-markall"
                        onClick={this.onMarkAllRead}
                        disabled={unreadCount === 0}
                    >
                        {t('layout.notifications.mark_all_read')}
                    </button>
                </div>
                {isLoading && (
                    <div className="notifications-panel-loading">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">
                                {t('layout.notifications.loading')}
                            </span>
                        </div>
                    </div>
                )}
                {!isLoading && notifications.length === 0 && (
                    <div className="notifications-panel-empty">
                        {filter === FILTER_UNREAD
                            ? t('layout.notifications.empty_unread')
                            : t('layout.notifications.empty')}
                    </div>
                )}
                {notifications.map((notification) => (
                    <NotificationItem
                        key={notification.id}
                        notification={notification}
                        onClick={this.onItemClick}
                        onMarkRead={this.onMarkOneRead}
                    />
                ))}
                {nextPageUrl && (
                    <button
                        type="button"
                        className="notifications-panel-loadmore"
                        onClick={this.onShowMore}
                        disabled={isLoadingMore}
                        aria-busy={isLoadingMore}
                    >
                        {isLoadingMore && (
                            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                        )}
                        {t('layout.notifications.show_more')}
                    </button>
                )}
            </div>
        );
    }
}

export default withTranslation()(NotificationsList);
